import type { DesktopUpdateActionResult, DesktopUpdateState } from "@cleo/contracts";
import { isWindowsPlatform } from "../lib/utils";

export type DesktopUpdateButtonAction = "download" | "install" | "none";

/**
 * The main process fills `downloadedVersion` from the updater's `update-downloaded`
 * event, which is dispatched on its own fiber. A download RPC can therefore resolve
 * before that write lands, so fall back to the version the download was started for.
 *
 * The version is all the toast names. It links nowhere: the release origin publishes
 * no notes page per `v<version>/` folder and nothing in the product points at GitHub
 * (`scripts/lib/release-origin.ts`, `GOALS/GOALS-RELEASE-DISTRIBUTION.html`).
 */
export function getDesktopUpdateDownloadedVersion(state: DesktopUpdateState): string | null {
  return state.downloadedVersion ?? state.availableVersion;
}

export function resolveDesktopUpdateButtonAction(
  state: DesktopUpdateState,
): DesktopUpdateButtonAction {
  if (state.downloadedVersion) {
    return "install";
  }
  if (state.status === "available") {
    return "download";
  }
  if (state.status === "error") {
    if (state.errorContext === "download" && state.availableVersion) {
      return "download";
    }
  }
  return "none";
}

export function shouldShowDesktopUpdateButton(state: DesktopUpdateState | null): boolean {
  if (!state || !state.enabled) {
    return false;
  }
  if (state.status === "downloading") {
    return true;
  }
  return resolveDesktopUpdateButtonAction(state) !== "none";
}

export function shouldShowArm64IntelBuildWarning(state: DesktopUpdateState | null): boolean {
  return state?.hostArch === "arm64" && state.appArch === "x64";
}

export function isDesktopUpdateButtonDisabled(state: DesktopUpdateState | null): boolean {
  return state?.status === "downloading";
}

export function getArm64IntelBuildWarningDescription(state: DesktopUpdateState): string {
  if (!shouldShowArm64IntelBuildWarning(state)) {
    return "This install is using the correct architecture.";
  }

  const action = resolveDesktopUpdateButtonAction(state);
  if (action === "download") {
    return "This Mac has Apple Silicon, but Cleo is still running the Intel build under Rosetta. Download the available update to switch to the native Apple Silicon build.";
  }
  if (action === "install") {
    return "This Mac has Apple Silicon, but Cleo is still running the Intel build under Rosetta. Restart to install the downloaded Apple Silicon build.";
  }
  return "This Mac has Apple Silicon, but Cleo is still running the Intel build under Rosetta. The next app update will replace it with the native Apple Silicon build.";
}

export function getDesktopUpdateButtonTooltip(state: DesktopUpdateState): string {
  // W-f5dee15c: a download a newer release replaced is said in the Desk's own plain line.
  if (state.status === "available" && state.replacedDownloadVersion && state.message) {
    return state.message;
  }
  if (state.status === "available") {
    return `Update ${state.availableVersion ?? "available"} ready to download`;
  }
  if (state.status === "downloading") {
    const progress =
      typeof state.downloadPercent === "number" ? ` (${Math.floor(state.downloadPercent)}%)` : "";
    return `Downloading update${progress}`;
  }
  if (state.status === "downloaded") {
    return `Update ${state.downloadedVersion ?? state.availableVersion ?? "ready"} downloaded. Click to restart and install.`;
  }
  if (state.status === "error") {
    if (state.errorContext === "download" && state.availableVersion) {
      return `Download failed for ${state.availableVersion}. Click to retry.`;
    }
    if (state.errorContext === "install" && state.downloadedVersion) {
      return `Install failed for ${state.downloadedVersion}. Click to retry.`;
    }
    return state.message ?? "Update failed";
  }
  return "Up to date";
}

export function getDesktopUpdateInstallConfirmationMessage(
  state: Pick<DesktopUpdateState, "availableVersion" | "downloadedVersion">,
  platform = "",
): string {
  const version = state.downloadedVersion ?? state.availableVersion;
  const windowsInstallWarning = isWindowsPlatform(platform)
    ? "\n\nOn Windows, Cleo may remain closed for several minutes while the update installs, and no installer window may appear. Cleo will reopen automatically when installation finishes."
    : "";
  return `Install update${version ? ` ${version}` : ""} and restart Cleo?\n\nAny running tasks will be interrupted. Make sure you're ready before continuing.${windowsInstallWarning}`;
}

export function getDesktopUpdateActionError(result: DesktopUpdateActionResult): string | null {
  if (!result.accepted || result.completed) return null;
  if (typeof result.state.message !== "string") return null;
  const message = result.state.message.trim();
  return message.length > 0 ? message : null;
}

export function shouldToastDesktopUpdateActionResult(result: DesktopUpdateActionResult): boolean {
  return getDesktopUpdateActionError(result) !== null;
}

export function shouldHighlightDesktopUpdateError(state: DesktopUpdateState | null): boolean {
  if (!state || state.status !== "error") return false;
  return state.errorContext === "download" || state.errorContext === "install";
}

export function canCheckForUpdate(state: DesktopUpdateState | null): boolean {
  if (!state || !state.enabled) return false;
  return (
    state.status !== "checking" &&
    state.status !== "downloading" &&
    state.status !== "downloaded" &&
    state.status !== "disabled"
  );
}

/**
 * What the one update notice says now, or null when there is nothing for the
 * person to do. A found update is said in words (20 September 2026: the
 * sidebar's icon alone went unseen), and the same notice then follows the
 * download to the end: on 22 September the person pressed a notice's Download
 * five times in twelve seconds because nothing on screen changed, and the
 * finished download only pointed at Settings.
 */
export type DesktopUpdateNotice =
  | { readonly kind: "available"; readonly version: string }
  | { readonly kind: "downloading"; readonly version: string; readonly percent: number | null }
  | { readonly kind: "downloaded"; readonly version: string }
  | { readonly kind: "waiting"; readonly version: string; readonly turns: number }
  | { readonly kind: "installing"; readonly version: string; readonly message?: string | null }
  | { readonly kind: "download-failed"; readonly version: string; readonly message: string | null }
  | { readonly kind: "install-failed"; readonly version: string; readonly message: string | null }
  | { readonly kind: "replaced"; readonly version: string; readonly replacedVersion: string };

export function desktopUpdateNotice(state: DesktopUpdateState | null): DesktopUpdateNotice | null {
  if (state === null || !state.enabled) return null;
  // Can 30: a pressed Update waits for Cleo's running turns instead of interrupting them.
  const turns = desktopUpdateWaitingTurns(state);
  const requestedVersion = getDesktopUpdateDownloadedVersion(state);
  if (turns > 0 && requestedVersion) {
    return { kind: "waiting", version: requestedVersion, turns };
  }
  if (state.status === "downloading" && state.availableVersion) {
    return { kind: "downloading", version: state.availableVersion, percent: state.downloadPercent };
  }
  if (state.status === "downloaded") {
    const version = getDesktopUpdateDownloadedVersion(state);
    if (!version) return null;
    if (state.errorContext === "install") {
      return { kind: "install-failed", version, message: state.message };
    }
    // While it waits, the Desk says why when it cannot tell whether work runs.
    return state.updateRequested
      ? { kind: "installing", version, message: state.message }
      : { kind: "downloaded", version };
  }
  // A pressed Update on a release not yet downloaded starts its download next.
  if (state.status === "available" && state.availableVersion && state.updateRequested) {
    return { kind: "downloading", version: state.availableVersion, percent: null };
  }
  if (state.status === "available" && state.availableVersion) {
    // A failed download settles back to "available" with its reason kept.
    if (state.errorContext === "download") {
      return { kind: "download-failed", version: state.availableVersion, message: state.message };
    }
    // W-f5dee15c: a newer release replaced the download, which was dropped.
    return state.replacedDownloadVersion
      ? {
          kind: "replaced",
          version: state.availableVersion,
          replacedVersion: state.replacedDownloadVersion,
        }
      : { kind: "available", version: state.availableVersion };
  }
  return null;
}

/** One phase of one version; a notice the person closed stays closed until the phase moves on. */
export function desktopUpdateNoticeKey(notice: DesktopUpdateNotice): string {
  return `${notice.kind}:${notice.version}`;
}

/** The general settings row the update notice's Settings opens at (`/settings/general#…`). */
export const DESKTOP_UPDATE_AUTOMATICALLY_TARGET_ID = "update-automatically";

/** Can 30: how many conversations a pressed Update still waits on; 0 when it is not waiting. */
export function desktopUpdateWaitingTurns(state: DesktopUpdateState): number {
  if (!state.updateRequested) return 0;
  const turns = state.waitingForTurns ?? 0;
  return Number.isFinite(turns) && turns > 0 ? Math.floor(turns) : 0;
}

const COUNT_WORDS = ["one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];

/** "one running conversation finishes", "three running conversations finish". */
export function desktopUpdateTurnsInWords(turns: number): string {
  const count = COUNT_WORDS[turns - 1] ?? String(turns);
  return turns === 1
    ? `${count} running conversation finishes`
    : `${count} running conversations finish`;
}

/**
 * Can 30: the Update button left of the bell. It stands while an update waits
 * for the person (found, downloading, downloaded, or pressed), including a
 * download or install that failed and can be tried again. Main keeps the
 * state, so the button outlives window closes and restarts with no storage of
 * its own, and the Desk reopens on the new version once it installs.
 */
export function shouldShowDeskUpdateButton(state: DesktopUpdateState | null): boolean {
  if (!state || !state.enabled) return false;
  if (state.updateRequested) return true;
  return state.status === "downloaded" || shouldShowDesktopUpdateButton(state);
}

export function deskUpdateButtonLabel(state: DesktopUpdateState): string {
  if (desktopUpdateWaitingTurns(state) > 0) return "Updating after Cleo finishes";
  if (state.status === "downloading") {
    return typeof state.downloadPercent === "number"
      ? `Updating ${Math.floor(state.downloadPercent)}%`
      : "Updating…";
  }
  if (state.updateRequested) return "Updating…";
  // Only when a restart really installs it: macOS's updater holds the download.
  if (state.status === "downloaded" && state.installsOnQuit === true) return "Restart to update";
  return "Update";
}

/** The button's hover line: the version first, and why it is still waiting when it is. */
export function deskUpdateButtonTitle(state: DesktopUpdateState): string {
  const version = getDesktopUpdateDownloadedVersion(state);
  const cleo = version ? `Cleo ${version}` : "A Cleo update";
  const turns = desktopUpdateWaitingTurns(state);
  if (turns > 0) return `${cleo} installs when ${desktopUpdateTurnsInWords(turns)}.`;
  if (state.status === "downloading") return `${cleo} is downloading`;
  if (!state.updateRequested && state.errorContext === "download") {
    return `${cleo} could not download. Click to try again.`;
  }
  if (!state.updateRequested && state.errorContext === "install") {
    return `${cleo} could not install. Click to try again.`;
  }
  // A pressed Update the Desk explains (it cannot tell whether work runs).
  if (state.updateRequested && state.status === "downloaded" && state.message) {
    return state.message;
  }
  return state.status === "downloaded" || state.downloadedVersion
    ? `${cleo} is ready`
    : `${cleo} is available`;
}

/**
 * Whether pressing Update now would do nothing new: the Desk is already on
 * it, or an older Desk (no `updateNow`) is downloading and has nothing to
 * install yet.
 */
export function isDeskUpdateButtonBusy(state: DesktopUpdateState, canUpdateNow: boolean): boolean {
  if (state.updateRequested) return true;
  return state.status === "downloading" && !canUpdateNow;
}
